import React, { useEffect, useRef, useState } from "react";
import PageLayout from "../common/PageLayout";
import { RiProfileLine } from "react-icons/ri";
import { useLocation } from "react-router-dom";
import {
  Alert,
  Backdrop,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { BsDownload, BsUpload } from "react-icons/bs";
import { SigningKeystoreRepository } from "../repositories/SigningKeystoreRepository";
import { useDispatch } from "react-redux";
import {
  notifyShowErrorMessage,
  notifyShowSuccessMessage,
} from "../../store/AlertSlice";
import { LOCALE } from "../../properties/Locale";

export default function UserProfile() {
  const location = useLocation();
  const refFileInput = useRef();
  const [request, setRequest] = useState();
  const [saving, setSaving] = useState(false);
  const [keystore, setKeystore] = useState();
  const [signingExtensionLoader, setSigningExtensionLoader] = useState(true);
  const [isSignerInstalled, setIsSignerInstalled] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    loadUserKeystore();
  }, []);

  useEffect(() => {
    setSigningExtensionLoader(true);
    const maxWaitTime = 3000;
    const interval = setInterval(() => {
      if (window.AuctaSignerApp) {
        clearInterval(interval);
        window.AuctaSignerApp.isInstalled((status) => {
          console.log("[external page] AuctaSignerApp.isInstalled ->", status);
          if (status === "OK") {
            console.log("AuctaSigner extension is installed.");
            setIsSignerInstalled(true);
            setSigningExtensionLoader(false);
          } else {
            console.log("AuctaSigner extension is not installed.");
            setIsSignerInstalled(false);
            setSigningExtensionLoader(false);
          }
        });
      }
    }, 200);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!window.AuctaSignerApp) {
        console.log("AuctaSigner extension is not installed.");
      }
      setSigningExtensionLoader(false);
    }, maxWaitTime);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  const handleUpload = () => {
    if (!request || !request.file || !request.password) {
      dispatch(notifyShowErrorMessage("Сите полиња се задолжителни"));
      return;
    }
    setSaving(true);
    SigningKeystoreRepository.uploadKeystore(request)
      .then((res) => {
        dispatch(notifyShowSuccessMessage(LOCALE.getString(res.data)));
        setSaving(false);
        setRequest();
        loadUserKeystore();
      })
      .catch((err) => {
        setSaving(false);
        dispatch(notifyShowErrorMessage(LOCALE.getString(err.response?.data)));
        console.log(err);
      });
  };

  const loadUserKeystore = () => {
    SigningKeystoreRepository.fetchUserKeystore()
      .then((res) => {
        setKeystore(res.data);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleDownload = () => {
    setSaving(true);
    SigningKeystoreRepository.downloadKeystore()
      .then((res) => {
        setSaving(false);
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", keystore.fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        setSaving(false);
        console.error(err);
      });
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(",")[1]; // remove metadata
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const testDocSigningWithExtension = () => {
    fetch("/sample/sample.pdf")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load sample.pdf");
        }
        return response.blob();
      })
      .then((blob) => {
        return blobToBase64(blob);
      })
      .then((base64Pdf) => {
        window.postMessage(
          {
            type: "FILE_UPLOAD",
            action: "WITH_PREVIEW",
            subaction: "SILENT_SIGN_PDF",
            fileName: "sample.pdf",
            fileType: "application/pdf",
            fileContent: base64Pdf,
          },
          "*",
        );
      })
      .catch((err) => {
        console.error("PDF signing failed:", err);
      });
  };

  return (
    <PageLayout
      title="Мој профил"
      Icon={RiProfileLine}
      prev={location.pathname}
    >
      {saving && (
        <Backdrop open={true} sx={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={12}>
          <Paper elevation={1} sx={{ padding: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Потпишување документи со прикачен дигитален потпис (.pfx, .p12)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} style={{ alignContent: "center" }}>
                    {" "}
                    <input
                      type="file"
                      accept=".pfx"
                      style={{ display: "none" }}
                      ref={refFileInput}
                      onChange={(e) => {
                        if (e.target.files.length > 0) {
                          const file = e.target.files[0];
                          setRequest((prev) => ({ ...prev, file: file }));
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      fullWidth
                      startIcon={<BsUpload />}
                      onClick={() => refFileInput.current.click()}
                    >
                      Прикачи датотека
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="password"
                      label="Лозинка "
                      variant="outlined"
                      fullWidth
                      value={request?.password || ""}
                      onChange={(e) =>
                        setRequest((prev) => ({
                          ...prev,
                          password: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                  {request?.file && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle1">
                        Избрана датотека: {request.file.name}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} style={{ textAlign: "right" }}>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleUpload}
                    >
                      Зачувај
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                {keystore && (
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={9}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: "bold",
                          color: "primary.main",
                          mb: 1,
                        }}
                      >
                        Активен дигитален потпис:{" "}
                        <span style={{ fontWeight: "normal", color: "#555" }}>
                          {keystore.fileName}
                        </span>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Button
                        variant="contained"
                        onClick={handleDownload}
                        fullWidth
                        startIcon={<BsDownload />}
                      >
                        Преземи
                      </Button>
                    </Grid>
                  </Grid>
                )}
                {!keystore && (
                  <Alert severity="info">
                    <Typography variant="subtitle2">
                      Немате поставено дигитален потпис.
                    </Typography>
                  </Alert>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Divider style={{ width: "100%", margin: "16px 0" }} />
        <Grid item xs={12} md={12}>
          <Paper elevation={1} sx={{ padding: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Потпишување документи со дигитален потпис (токен - екстензија)
            </Typography>
            {signingExtensionLoader && (
              <Grid container spacing={2}>
                <Grid item xs={12} style={{ textAlign: "center" }}>
                  <CircularProgress />
                </Grid>
              </Grid>
            )}
            {!isSignerInstalled && !signingExtensionLoader && (
              <Alert
                severity="error"
                variant="filled"
                sx={{ padding: "4px 8px" }}
              >
                За потпишување на документите, потребно е да ја инсталирате
                екстензијата за потпишување.{" "}
                <a
                  target="_blank"
                  style={{ color: "white", fontWeight: "bold" }}
                  href="https://chromewebstore.google.com/detail/aucta-signer-v2/gdekohcdoabefpfpjcanibkkhfinifmn"
                >
                  Преземете ја екстензијата тука
                </a>
                <br />
                По инсталирањето на екстензијата освежете ја страницата{" "}
                <a
                  href="#"
                  style={{ color: "white", fontWeight: "bold" }}
                  onClick={() => window.location.reload()}
                >
                  тука
                </a>
                .
              </Alert>
            )}
            {isSignerInstalled && !signingExtensionLoader && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Alert
                    severity="success"
                    variant="filled"
                    sx={{ padding: "4px 8px" }}
                  >
                    Екстензијата за потпишување е инсталирана и спремна за
                    употреба.
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <Alert
                    severity="warning"
                    variant="filled"
                    sx={{ padding: "4px 8px" }}
                  >
                    Освен екстензијата, потребно е да имате инсталирано и
                    помошна апликација за комуникација со екстензијата. Истата
                    може да ја преземете тука:{" "}
                    <a
                      style={{ color: "white", fontWeight: "bold" }}
                      href="/install/aucta-signer-native-v2-installer.msi"
                      download
                    >
                      aucta-signer-native-v2-installer.msi
                    </a>
                  </Alert>
                </Grid>
                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={() => {
                      testDocSigningWithExtension();
                    }}
                  >
                    Тестирај потпишување документ
                  </Button>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>
    </PageLayout>
  );
}
